import { useState } from 'react';
import { ImageFragment } from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { ColorDetail } from '@/ui/components/pages/ProductDetail/types/ColorDetail';
import { SelectedProductVariants } from '@/ui/components/pages/ProductDetail/types/SelectedProductVariants';
import { Product } from '@/ui/components/pages/products/types/Product';
import type { ProductBadgeModel } from '@/models/product-badge-model';
import { useProductRuleService } from '@/ui/providers/product-rules-provider';
import { useLocalization } from '@/ui/providers/localizationProvider';
import { snapSelectionToCountry } from '@/ui/services/utils/bundle-country-snap';

export interface BundleCardSelection {
  image?: ImageFragment;
  price: number;
  priceWithoutDiscount: number;
  href?: string;
  // Colours of the set's first colour slot; empty for a product or a set without one
  colorOptions: ColorDetail[];
  selectedColorKey?: string;
  selectColor: (color: ColorDetail) => void;
  badges: ProductBadgeModel[];
}

/**
 * The colour a set card shows and what follows from it: the combination image, the price,
 * the badges and the link that opens the set with that colour preselected. Only the first colour
 * slot is offered, one row of dots per card. A product without a set keeps its entry as is.
 */
export function useBundleCardSelection(product: Product): BundleCardSelection {
  const { bundle } = product;
  const { country } = useLocalization();
  const productRuleService = useProductRuleService();
  // A default the country hides starts on its sole visible option, as the set's page does,
  // so the card shows the image, price and link of what the visitor can buy.
  const [selected, setSelected] = useState<SelectedProductVariants>(() => {
    if (!bundle) {
      return {};
    }

    return snapSelectionToCountry(bundle.products, bundle.defaultBundleProductVariants, country);
  });

  const colorSlot = bundle?.products.find((slot) => slot.isColorSlot());
  const slotOptions = colorSlot?.getSlotOptions(country);
  const colorOptions = slotOptions?.kind === 'color' ? slotOptions.colors : [];

  const selectColor = (color: ColorDetail) => {
    const variant = colorSlot?.getVariantForOption(color);
    if (!colorSlot || !variant) {
      return;
    }

    setSelected((previous) => ({ ...previous, [colorSlot.bundleProductInstanceId]: variant }));
  };

  if (!bundle) {
    return {
      image: product.image,
      price: product.price,
      priceWithoutDiscount: product.priceWithoutDiscount,
      href: product.path,
      colorOptions,
      selectColor,
      badges: product.badges,
    };
  }

  return {
    image: bundle.getImagesForSelection(selected)[0],
    price: bundle.getFinalPrice(selected),
    priceWithoutDiscount: bundle.getTotalOriginalPrice(selected),
    href: bundle.getLinkPathForSelection(selected),
    colorOptions,
    selectedColorKey: colorSlot?.resolveVariant(selected).color?.name,
    selectColor,
    badges: productRuleService.getBundleBadges(bundle.getConfiguration(selected)),
  };
}
